import React, { useRef, useEffect } from "react";
import {
  format,
  addMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  isSameDay,
  isBefore,
  startOfDay,
} from "date-fns";

const MobileScrollingCalendar = ({
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  minDate,
}) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!minDate || !scrollRef.current) return;

    // Find the month element that matches minDate's month
    const monthIndex =
      minDate.getMonth() -
      new Date().getMonth() +
      (minDate.getFullYear() - new Date().getFullYear()) * 12;

    const monthElements = scrollRef.current.querySelectorAll(".month-block");
    if (monthElements[monthIndex]) {
      monthElements[monthIndex].scrollIntoView({ behavior: "smooth" });
    }
  }, [minDate]);

  // Generates 12 months for a continuous vertical scroll
  const monthsToRender = Array.from({ length: 12 }, (_, i) =>
    addMonths(new Date(), i),
  );

  const renderMonth = (monthDate) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const dateRange = eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    });

    return (
      <div key={monthDate.toString()} className="month-block mb-10 px-2">
        {/* Month Header */}
        <h3 className="text-lg font-heading font-bold text-navy mb-4 px-2">
          {format(monthDate, "MMMM yyyy")}
        </h3>

        {/* Weekday Headers */}
        <div className="grid grid-cols-7 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="text-center text-[11px] font-bold text-navy/30 uppercase tracking-widest"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7">
          {dateRange.map((day, idx) => {
            // Check for individual selection only
            // const isSelected =
            //   (selectedStartDate && isSameDay(day, selectedStartDate)) ||
            //   (selectedEndDate && isSameDay(day, selectedEndDate));

            const isPast = isBefore(day, startOfDay(minDate || new Date()));
            const isCurrentMonth = day.getMonth() === monthDate.getMonth();
            const today = new Date();
            const isToday = isSameDay(day, today);
            const isStart =
              selectedStartDate && isSameDay(day, selectedStartDate);
            const isEnd = selectedEndDate && isSameDay(day, selectedEndDate);

            const isInRange =
              selectedStartDate &&
              selectedEndDate &&
              day > selectedStartDate &&
              day < selectedEndDate;

            return (
              <div
                key={idx}
                onClick={() => !isPast && isCurrentMonth && onDateSelect(day)}
                className={`
                  h-12 flex items-center justify-center cursor-pointer
                  ${!isCurrentMonth ? "invisible" : ""}
                  ${isPast ? "text-gray-200 pointer-events-none" : "text-navy"}
                `}
              >
                <div
                  className={`flex items-center justify-center text-sm font-bold transition-all duration-200 
                  ${
                    isPast
                      ? "w-10 h-10"
                      : isStart && isEnd
                        ? "bg-gold text-white rounded-lg w-10 h-10"
                        : isStart
                          ? "bg-gold text-white rounded-l-lg w-full h-10"
                          : isEnd
                            ? "bg-gold text-white rounded-r-lg w-full h-10"
                            : isInRange
                              ? "bg-gold/20 text-gold w-full h-10"
                              : isToday
                                ? "border border-gold text-navy rounded-lg w-10 h-10"
                                : "w-10 h-10 hover:bg-gray-50 rounded-lg"
                  }
          `}
                >
                  {isCurrentMonth ? format(day, "d") : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div ref={scrollRef} className="flex flex-col py-4 overflow-y-auto">
      {monthsToRender.map((month) => renderMonth(month))}
    </div>
  );
};

export default MobileScrollingCalendar;
